import React, { useState, useEffect } from 'react';
import { X, Search, ArrowUpRight } from 'lucide-react';
import AddressFinder from './AddressFinder';
import userService from '../services/userService';

const AddCustomerModal = ({ onClose, onSubmit, customer, isEditing = false, userProfile }) => {
  const isAdmin = userProfile?.role === 'admin';
  const [franchisees, setFranchisees] = useState([]);

  useEffect(() => {
    if (isAdmin && !isEditing) {
      userService.getFranchisees().then(data => {
        setFranchisees(Array.isArray(data) ? data : data?.franchisees || []);
      }).catch(err => console.error('Error loading franchisees:', err));
    }
  }, [isAdmin, isEditing]);
  const [formData, setFormData] = useState({
    name: customer?.name || '',
    first_name: customer?.first_name || '',
    last_name: customer?.last_name || '',
    company_name: customer?.company_name || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    address_line1: customer?.address_line1 || '',
    address_line2: customer?.address_line2 || '',
    address_city: customer?.address_city || '',
    address_county: customer?.address_county || '',
    address_postcode: customer?.address_postcode || '',
    address_country: customer?.address_country || 'United Kingdom',
    address_uprn: customer?.address_uprn || '',
    notes: customer?.notes || '',
    franchisee_id: ''
  });

  const [errors, setErrors] = useState({});
  const [addressFinderState, setAddressFinderState] = useState({});

  // Update form data when customer prop changes (for editing mode)
  useEffect(() => {
    if (customer && isEditing) {
      setFormData({
        name: customer.name || '',
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        company_name: customer.company_name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address_line1: customer.address_line1 || '',
        address_line2: customer.address_line2 || '',
        address_city: customer.address_city || '',
        address_county: customer.address_county || '',
        address_postcode: customer.address_postcode || '',
        address_country: customer.address_country || 'United Kingdom',
        address_uprn: customer.address_uprn || '',
        notes: customer.notes || ''
      });
    }
  }, [customer, isEditing]);

  // Compute display name: company_name takes priority, then "first last", then just first or last
  const computeDisplayName = (data) => {
    const fullName = [data.first_name, data.last_name].filter(s => s?.trim()).join(' ');
    if (data.company_name?.trim() && fullName) {
      return `${data.company_name.trim()} - ${fullName}`;
    }
    if (data.company_name?.trim()) return data.company_name.trim();
    return fullName;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    // Clear the "name required" error when typing in relevant fields
    if (['first_name', 'last_name', 'company_name'].includes(name) && errors.name) {
      setErrors(prev => ({ ...prev, name: '' }));
    }
  };

  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      ...addressData
    }));
    // Clear any address-related errors
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.address;
      return newErrors;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Need either a name or company name to derive display name
    const hasName = formData.first_name?.trim() || formData.last_name?.trim();
    const hasCompany = formData.company_name?.trim();
    if (!hasName && !hasCompany) {
      newErrors.name = 'Either a name or company name is required';
    }

    // Admin must assign to a franchise
    if (isAdmin && !isEditing && !formData.franchisee_id) {
      newErrors.franchisee_id = 'Please assign this customer to a franchise';
    }

    // Only validate email format if it's provided
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        const submitData = { ...formData };
        submitData.name = computeDisplayName(submitData);
        await onSubmit(submitData);
      } catch (error) {
        // Error toast handled by caller — keep modal open for retry
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex justify-between items-center p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-pest-blue to-pest-teal">
          <h2 className="text-xl md:text-2xl font-bold text-white">
            {isEditing
              ? 'Edit Customer'
              : `Add New Customer${computeDisplayName(formData) ? ': ' + computeDisplayName(formData) : ''}`
            }
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors duration-200"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-4 md:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
            {/* Left Column - Contact Information */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-pest-blue">Contact Information</h3>
                </div>

                <div className="p-6 space-y-4">
                  {isAdmin && !isEditing && (
                    <div>
                      <label htmlFor="franchisee_id" className="block text-sm font-medium text-gray-700 mb-2">
                        Assign to Franchise *
                      </label>
                      <select
                        id="franchisee_id"
                        name="franchisee_id"
                        value={formData.franchisee_id}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors ${
                          errors.franchisee_id ? 'border-red-500' : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <option value="">Select franchise...</option>
                        {franchisees.map(f => (
                          <option key={f.id} value={f.id}>{f.name}{f.company_name ? ` — ${f.company_name}` : ''}</option>
                        ))}
                      </select>
                      {errors.franchisee_id && <p className="text-red-500 text-sm mt-2">{errors.franchisee_id}</p>}
                    </div>
                  )}

                  {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors"
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors"
                        placeholder="Last name"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company_name"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors"
                      placeholder="Enter company name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors ${
                        errors.email ? 'border-red-500' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      placeholder="Enter email address"
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-2">{errors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="text"
                      inputMode="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors"
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Address & Notes */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-pest-blue">Address</h3>
                    {addressFinderState.uiState === 'manual' && addressFinderState.switchToAddressSearch && (
                      <button
                        type="button"
                        onClick={addressFinderState.switchToAddressSearch}
                        className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-pest-teal to-teal-600 text-white border-none rounded-lg text-xs font-semibold cursor-pointer transition-all duration-200 hover:from-teal-600 hover:to-teal-700 hover:shadow-lg"
                        aria-label="Switch back to address lookup"
                      >
                        <Search size={14} aria-hidden="true" />
                        Use Address Lookup
                        <ArrowUpRight size={14} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="p-6">
                  <AddressFinder
                    initialAddress={{
                      address_line1: formData.address_line1,
                      address_line2: formData.address_line2,
                      address_city: formData.address_city,
                      address_county: formData.address_county,
                      address_postcode: formData.address_postcode,
                      address_country: formData.address_country,
                      address_uprn: formData.address_uprn
                    }}
                    onAddressSelect={handleAddressSelect}
                    onError={(error) => {
                      setErrors(prev => ({
                        ...prev,
                        address: error
                      }));
                    }}
                    onStateChange={setAddressFinderState}
                    showManualEntry={true}
                    autoSelectSingle={true}

                  />
                  {errors.address && <p className="text-red-500 text-sm mt-2">{errors.address}</p>}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-pest-blue">Additional Information</h3>
                </div>
                
                <div className="p-6">
                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors resize-none"
                      placeholder="Additional notes about the customer..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-4 space-y-3 sm:space-y-0">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-lg btn-outline"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-lg btn-primary"
              >
                {isEditing ? 'Update Customer' : 'Add Customer'}
              </button>
            </div>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCustomerModal;